"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import axios from "axios";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "2px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

// Helper: normalize value for comparison (handles null, undefined, empty string)
const normalizeForCompare = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value).trim();
};

// Helper: check if two values are effectively equal
const valuesEqual = (a, b) => {
  return normalizeForCompare(a) === normalizeForCompare(b);
};

// Helper: get vendor ID as string
const getVendorId = (vendor) => {
  if (!vendor) return null;
  if (typeof vendor === "object" && vendor._id) {
    return String(vendor._id);
  }
  return String(vendor);
};

// Helper: safe number parsing
const safeNumber = (val, defaultVal = 0) => {
  if (val === undefined || val === null || val === "") return defaultVal;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? defaultVal : parsed;
};

// =====================================================
// Coverage Calculation Helpers (matching material order logic)
// =====================================================
const parseToFeet = (str) => {
  if (!str) return 0;
  const s = String(str).trim();

  // Check for feet and inches: 4'6" or 4' 6"
  const feetInchMatch = s.match(/^([\d.]+)'?\s*([\d.]+)"?$/);
  if (feetInchMatch) {
    const feet = parseFloat(feetInchMatch[1]) || 0;
    const inches = parseFloat(feetInchMatch[2]) || 0;
    return feet + inches / 12;
  }

  // Check for just feet: 4' or 4
  const feetMatch = s.match(/^([\d.]+)'?$/);
  if (feetMatch) {
    return parseFloat(feetMatch[1]) || 0;
  }

  // Check for just inches: 48"
  const inchMatch = s.match(/^([\d.]+)"$/);
  if (inchMatch) {
    return (parseFloat(inchMatch[1]) || 0) / 12;
  }

  // Plain number
  return parseFloat(s) || 0;
};

const isSFUnit = (uom) => {
  const uomLower = (uom || "").toLowerCase();
  return (
    uomLower === "sf" ||
    uomLower.includes("square") ||
    uomLower.includes("sq ft") ||
    uomLower.includes("sq.ft")
  );
};

// Helper: get material cost from item (checks multiple possible fields)
const getMaterialCost = (item) => {
  if (!item || item.category !== "M") return 0;
  // Check in order of priority: latestPrice, price, cost
  if (item.latestPrice !== undefined && item.latestPrice !== null) {
    return safeNumber(item.latestPrice, 0);
  }
  if (item.price !== undefined && item.price !== null) {
    return safeNumber(item.price, 0);
  }
  return safeNumber(item.cost, 0);
};

// Helper: get labor unit rate from item (checks multiple possible fields)
const getLaborUnitRate = (item) => {
  if (!item || item.category !== "L") return 0;
  // Check unitRate first
  if (item.unitRate !== undefined && item.unitRate !== null) {
    return safeNumber(item.unitRate, 0);
  }
  // Fall back to calculating from amount/quantity
  const qty = safeNumber(item.quantity, 0);
  const amount = safeNumber(item.amount, 0);
  if (qty > 0) {
    return Number((amount / qty).toFixed(4));
  }
  // Try price field
  if (item.price !== undefined && item.price !== null) {
    return safeNumber(item.price, 0);
  }
  return 0;
};

const isLFUnit = (uom) => {
  const uomLower = (uom || "").toLowerCase();
  return (
    uomLower === "lf" ||
    uomLower.includes("linear") ||
    uomLower.includes("lin ft") ||
    uomLower.includes("lin.ft")
  );
};

const calculateCoverageFromSize = (size, uom) => {
  if (!size) return 1;

  if (isSFUnit(uom)) {
    const parts = String(size)
      .replace(/×/g, "x")
      .split(/\s*[xX]\s*/);
    if (parts.length === 2) {
      const width = parseToFeet(parts[0].trim());
      const height = parseToFeet(parts[1].trim());
      if (width > 0 && height > 0) {
        return Number((width * height).toFixed(2));
      }
    }
  }

  if (isLFUnit(uom)) {
    const length = parseToFeet(size);
    if (length > 0) {
      return Number(length.toFixed(2));
    }
  }

  return 1;
};

// Check if UoM uses coverage-based pricing
const usesCoverage = (uom) => {
  return isSFUnit(uom) || isLFUnit(uom);
};

const VarianceModal = ({
  budgetId,
  lineItems,
  vendors,
  bidAreaOptions,
  projectId,
  onClose,
  setProject,
  onVarianceCreated,
  setDialog,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [varianceData, setVarianceData] = useState({});
  const [allChanges, setAllChanges] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkedLaborItems, setLinkedLaborItems] = useState([]);
  const [selectedLaborItems, setSelectedLaborItems] = useState({});
  const [isLoadingLabor, setIsLoadingLabor] = useState(false);
  const allChangesRef = useRef([]);

  // Store original values for each item to prevent them from changing
  const originalValuesRef = useRef(new Map());

  // Project-wide net quantities
  const [projectNetQuantities, setProjectNetQuantities] = useState(new Map());
  const [isLoadingNetQty, setIsLoadingNetQty] = useState(false);

  // Enriched line items with proper order quantities from net-assemblies
  const [enrichedLineItems, setEnrichedLineItems] = useState([]);

  // Filter out closed-out items - they cannot have variances created
  // Use enriched items if available, otherwise fall back to raw lineItems
  const currentItems = useMemo(() => {
    const sourceItems =
      enrichedLineItems.length > 0 ? enrichedLineItems : lineItems;
    return sourceItems.filter((item) => !item.closedOutBySizeChange);
  }, [lineItems, enrichedLineItems]);

  // Check if any items were filtered out
  const closedOutCount = lineItems.length - currentItems.length;

  const currentItem = currentItems[currentIndex];
  const isLastItem = currentIndex === currentItems.length - 1;

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  // Cache original values for all items on mount
  useEffect(() => {
    currentItems.forEach((item) => {
      const itemId = item._id || item.key || item.originalLineItemId;
      if (itemId && !originalValuesRef.current.has(itemId)) {
        const isMaterial = item.category === "M";
        originalValuesRef.current.set(itemId, {
          // Material fields
          cost: getMaterialCost(item),
          size: item.size || "",
          requiredQty: safeNumber(item.requiredQty, 0),
          vendor: getVendorId(item.vendor),
          bidArea: item.bidArea || "",
          // Labor fields
          unitRate: getLaborUnitRate(item),
          quantity: safeNumber(item.quantity, 0),
          // Common
          category: item.category,
        });
      }
    });
  }, [currentItems]);

  // Helper to get original values for current item
  const getOriginalValues = (item) => {
    if (!item) return null;
    const itemId = item._id || item.key || item.originalLineItemId;
    if (itemId && originalValuesRef.current.has(itemId)) {
      return originalValuesRef.current.get(itemId);
    }
    // Fallback to calculating from item
    return {
      cost: getMaterialCost(item),
      size: item.size || "",
      requiredQty: safeNumber(item.requiredQty, 0),
      vendor: getVendorId(item.vendor),
      bidArea: item.bidArea || "",
      unitRate: getLaborUnitRate(item),
      quantity: safeNumber(item.quantity, 0),
      category: item.category,
    };
  };

  // Fetch project-wide net quantities on mount
  useEffect(() => {
    const fetchNetQuantities = async () => {
      if (!projectId) return;

      setIsLoadingNetQty(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/net-assemblies/project/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              category: "M",
              includePrices: true,
            },
          },
        );

        const netQuantitiesMap = new Map();
        if (response.data.data && Array.isArray(response.data.data)) {
          response.data.data.forEach((item) => {
            // Store under multiple possible keys for flexible lookup
            // Match the key logic from PODetailView
            const primaryKey = (
              item.originalLineItemId ||
              item.key ||
              item._id ||
              ""
            ).toString();

            const netData = {
              netRequiredQty: item.netRequiredQty || 0,
              qtyOrdered: item.qtyOrdered || 0,
              qtyPending: item.qtyPending || 0,
              netRemaining: item.netRemaining || 0,
              baseRequiredQty: item.baseRequiredQty || 0,
              deltaQty: item.deltaQty || 0,
              hasVariance: item.hasVariance || false,
              latestSize: item.latestSize || "",
              latestPrice: item.latestPrice || 0,
              // Include additional fields from PODetailView
              coveragePerUnit:
                item.coveragePerUnit || item.latestCoveragePerUnit || "",
              price: item.price || item.latestPrice || 0,
            };

            // Store under the primary key
            if (primaryKey) {
              netQuantitiesMap.set(primaryKey, netData);
            }

            // Also store under _id if different from primary key
            if (item._id && item._id.toString() !== primaryKey) {
              netQuantitiesMap.set(item._id.toString(), netData);
            }

            // Also store under key if different
            if (item.key && item.key.toString() !== primaryKey) {
              netQuantitiesMap.set(item.key.toString(), netData);
            }
          });
        }
        setProjectNetQuantities(netQuantitiesMap);

        console.log(
          `[VarianceModal] Loaded ${netQuantitiesMap.size} net quantity entries`,
        );

        // Debug: Log first few entries to see the key structure
        if (netQuantitiesMap.size > 0) {
          const entries = Array.from(netQuantitiesMap.entries()).slice(0, 3);
          console.log("[VarianceModal] Sample net quantity entries:", entries);
        }
      } catch (err) {
        console.error("Error fetching net quantities:", err);
      } finally {
        setIsLoadingNetQty(false);
      }
    };

    fetchNetQuantities();
  }, [projectId]);

  // Enrich line items with net quantities data when both are available
  useEffect(() => {
    if (projectNetQuantities.size === 0 || !lineItems.length) {
      setEnrichedLineItems([]);
      return;
    }

    const enriched = lineItems.map((item) => {
      // Try to find net data for this item
      const netData = getNetItemData(item);

      if (netData) {
        return {
          ...item,
          // Override with net quantities data
          qtyOrdered: netData.qtyOrdered || 0,
          qtyPending: netData.qtyPending || 0,
          netRequiredQty:
            netData.netRequiredQty || item.requiredQty || item.quantity || 0,
          netRemaining: netData.netRemaining || 0,
          // Mark that we found net data
          _hasNetData: true,
        };
      }

      return {
        ...item,
        // Keep original values but add fallbacks
        qtyOrdered: item.qtyOrdered || 0,
        qtyPending: item.qtyPending || 0,
        _hasNetData: false,
      };
    });

    setEnrichedLineItems(enriched);

    // Debug log
    const withNetData = enriched.filter((e) => e._hasNetData).length;
    console.log(
      `[VarianceModal] Enriched ${enriched.length} items, ${withNetData} have net data`,
    );
  }, [projectNetQuantities, lineItems]);

  useEffect(() => {
    allChangesRef.current = allChanges;
  }, [allChanges]);

  // Get net quantity data for a line item
  // Must use the same key logic as the net-assemblies endpoint: originalLineItemId || _id
  const getNetItemData = (item) => {
    if (!item) return null;

    // Try multiple possible keys to find the net data
    const possibleKeys = [
      item.originalLineItemId,
      item._id,
      item.key,
      item.lineItemId,
    ]
      .filter(Boolean)
      .map((k) => k.toString());

    for (const key of possibleKeys) {
      const data = projectNetQuantities.get(key);
      if (data) return data;
    }

    return null;
  };

  // Updated calculateChanges function with reduction validation
  const calculateChanges = (item, editedValues) => {
    const isMaterial = item.category === "M";
    const uom = item.container || item.uom || "";
    const origValues = getOriginalValues(item);

    // Use the improved getNetItemData that tries multiple keys
    const netData = getNetItemData(item) || {};

    // Get quantities - prefer net data, fallback to item's own properties
    const netRequiredQty =
      netData.netRequiredQty ??
      (isMaterial ? item.requiredQty : item.quantity) ??
      0;

    // IMPORTANT: Also check item's own qtyOrdered/qtyPending as fallback
    const qtyOrdered = netData.qtyOrdered ?? item.qtyOrdered ?? 0;
    const qtyPending = netData.qtyPending ?? item.qtyPending ?? 0;
    const netRemaining = Math.max(0, netRequiredQty - qtyOrdered - qtyPending);

    // Minimum quantity is what's already committed (ordered + pending)
    const minAllowedQty = qtyOrdered + qtyPending;

    const originalSize = origValues?.size || item.size || "";
    const newSize = editedValues.size ?? originalSize;
    const sizeChanged =
      !valuesEqual(originalSize, newSize) && originalSize && newSize;

    const originalCoverage = item.coveragePerUnit
      ? parseFloat(item.coveragePerUnit)
      : calculateCoverageFromSize(originalSize, uom);

    const newCoverage = sizeChanged
      ? calculateCoverageFromSize(newSize, uom)
      : originalCoverage;

    const originalQty = isMaterial
      ? (origValues?.requiredQty ?? Number(item.requiredQty || 0))
      : (origValues?.quantity ?? Number(item.quantity || 0));

    const newQty = isMaterial
      ? Number(editedValues.requiredQty ?? originalQty)
      : Number(editedValues.quantity ?? originalQty);

    // Check if reduction goes below minimum
    const isReduction = newQty < originalQty;
    const isBelowMinimum = newQty < minAllowedQty;

    const originalPrice = isMaterial
      ? (origValues?.cost ?? getMaterialCost(item))
      : (origValues?.unitRate ?? getLaborUnitRate(item));

    const newPrice = isMaterial
      ? safeNumber(editedValues.cost, originalPrice)
      : Number(safeNumber(editedValues.unitRate, originalPrice).toFixed(4));

    const priceChanged = Math.abs(newPrice - originalPrice) > 0.001;
    const qtyChanged = Math.abs(newQty - originalQty) > 0.001;

    // Check for vendor change
    const originalVendorId = origValues?.vendor || getVendorId(item.vendor);
    const newVendorId = getVendorId(editedValues.vendor);
    const vendorChanged =
      newVendorId !== undefined &&
      newVendorId !== null &&
      newVendorId !== originalVendorId;

    const newSpec = {};
    if (isMaterial) {
      if (sizeChanged) {
        newSpec.size = newSize;
        newSpec.coveragePerUnit = String(newCoverage);
      }
      if (vendorChanged) {
        newSpec.vendor = editedValues.vendor || null;
      }
      if (priceChanged) {
        newSpec.price = newPrice;
        newSpec.cost = newPrice;
      }
    } else {
      if (priceChanged) newSpec.unitRate = newPrice;
    }

    const hasSpecChange = Object.keys(newSpec).length > 0;

    let deltaQty = 0;
    let costDiff = 0;
    let coverageDelta = 0;
    let closeoutAmount = 0;
    let newSpecAmount = 0;

    // ────────────────────────────────────────────────────────────────
    // LABOR ITEMS: Simple calculation (qty × unitRate)
    // ────────────────────────────────────────────────────────────────
    if (!isMaterial) {
      deltaQty = newQty - originalQty;
      const originalAmount = originalQty * originalPrice;
      const newAmount = newQty * newPrice;
      costDiff = Math.round((newAmount - originalAmount) * 100) / 100;
      closeoutAmount = originalAmount;
      newSpecAmount = newAmount;
    }
    // ────────────────────────────────────────────────────────────────
    // MATERIAL ITEMS: Coverage-aware calculations
    // ────────────────────────────────────────────────────────────────
    else if (sizeChanged && usesCoverage(uom)) {
      closeoutAmount = netRemaining * originalCoverage * originalPrice;
      newSpecAmount = newQty * newCoverage * newPrice;
      costDiff = Math.round((newSpecAmount - closeoutAmount) * 100) / 100;
      coverageDelta = newQty * newCoverage - netRemaining * originalCoverage;
      deltaQty = newQty;
    } else if (priceChanged && !qtyChanged && netRemaining > 0) {
      const affectedCoverage = usesCoverage(uom) ? originalCoverage : 1;
      closeoutAmount = netRemaining * affectedCoverage * originalPrice;
      newSpecAmount = netRemaining * affectedCoverage * newPrice;
      costDiff = Math.round((newSpecAmount - closeoutAmount) * 100) / 100;
      deltaQty = 0;
    } else if (qtyChanged || priceChanged) {
      // For quantity reductions, deltaQty will be negative
      deltaQty = newQty - originalQty;

      // Calculate cost difference based on the delta
      const effectiveCoverage = usesCoverage(uom) ? originalCoverage : 1;

      if (isReduction) {
        // For reductions: negative delta × coverage × original price = negative cost (savings)
        costDiff = deltaQty * effectiveCoverage * originalPrice;
      } else {
        // For overages: positive delta × coverage × newPrice = positive cost
        costDiff = deltaQty * effectiveCoverage * newPrice;
      }

      // If price also changed, add the price difference for remaining qty
      if (priceChanged && netRemaining > 0 && !isReduction) {
        const priceCloseout = netRemaining * effectiveCoverage * originalPrice;
        const priceNew = netRemaining * effectiveCoverage * newPrice;
        costDiff += priceNew - priceCloseout;
      }

      costDiff = Math.round(costDiff * 100) / 100;
      coverageDelta = deltaQty * effectiveCoverage;
    }

    return {
      deltaQty,
      newSpec,
      costDiff,
      coverageDelta,
      sizeChanged,
      priceChanged,
      vendorChanged,
      hasSpecChange,
      originalCoverage,
      newCoverage,
      netRemaining,
      netRequiredQty,
      qtyOrdered,
      qtyPending,
      closeoutAmount,
      newSpecAmount,
      originalPrice,
      newPrice,
      originalVendorId,
      editedValues: { ...editedValues },
      // New fields for reduction validation
      minAllowedQty,
      isReduction,
      isBelowMinimum,
      originalQty,
      newQty,
    };
  };

  // Fetch linked labor items when material item changes
  useEffect(() => {
    const fetchLinkedLabor = async () => {
      if (
        !currentItem ||
        currentItem.category !== "M" ||
        !currentItem.laborId
      ) {
        setLinkedLaborItems([]);
        return;
      }

      // Only fetch if laborId is valid (L1-L5 pattern)
      const laborId = currentItem.laborId;
      if (!/^L[1-5]$/i.test(String(laborId).trim())) {
        setLinkedLaborItems([]);
        return;
      }

      setIsLoadingLabor(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/budget2/${projectId}/linked-labor/${currentItem.assemblyId}/${laborId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data.success && response.data.laborItems) {
          setLinkedLaborItems(response.data.laborItems);

          // Check if we have saved selections for this item
          const existingChange = allChangesRef.current.find(
            (c) => c.lineItemId === currentItem._id,
          );

          if (existingChange?.savedSelectedLaborItems) {
            // Restore saved selections
            setSelectedLaborItems(existingChange.savedSelectedLaborItems);
          } else {
            // Auto-select all labor items by default for new items
            const defaultSelected = {};
            response.data.laborItems.forEach((labor) => {
              defaultSelected[labor._id] = true;
            });
            setSelectedLaborItems(defaultSelected);
          }
        } else {
          setLinkedLaborItems([]);
        }
      } catch (err) {
        console.error("Error fetching linked labor:", err);
        setLinkedLaborItems([]);
      } finally {
        setIsLoadingLabor(false);
      }
    };

    fetchLinkedLabor();
  }, [
    currentItem?._id,
    currentItem?.laborId,
    currentItem?.assemblyId,
    projectId,
  ]);

  // Updated useEffect for initializing varianceData
  useEffect(() => {
    if (currentItem) {
      const effectiveItemId =
        currentItem._id ||
        currentItem.key ||
        currentItem.originalLineItemId ||
        currentItem.lineItemId;

      const existingChange = allChangesRef.current.find(
        (c) => c.lineItemId === effectiveItemId,
      );

      if (existingChange && existingChange.savedVarianceData) {
        // Restore the ENTIRE saved varianceData - this preserves all user edits
        setVarianceData({ ...existingChange.savedVarianceData });

        // Restore selected labor items
        if (existingChange.savedSelectedLaborItems) {
          setSelectedLaborItems({ ...existingChange.savedSelectedLaborItems });
        }
      } else {
        // Initialize with original values for new items
        const vendorId = getVendorId(currentItem.vendor);
        const origValues = getOriginalValues(currentItem);

        setVarianceData((prev) => ({
          varianceTitle: prev.varianceTitle || "",
          varianceDescription: prev.varianceDescription || "",
          // Material fields - use getMaterialCost to get the correct price
          ...(currentItem.category === "M" && {
            materialCode: currentItem.materialCode || "",
            itemDescription:
              currentItem.itemDescription ||
              currentItem.costCodeDescription ||
              "",
            size: currentItem.latestSize || currentItem.size || "",
            container: currentItem.container || "",
            bidArea: currentItem.bidArea || "",
            vendor: vendorId,
            requiredQty:
              currentItem.netRequiredQty ?? currentItem.requiredQty ?? 0,
            cost: origValues?.cost ?? getMaterialCost(currentItem),
          }),
          // Labor fields
          ...(currentItem.category === "L" && {
            costCodeDescription: currentItem.costCodeDescription || "",
            uom: currentItem.uom || "",
            quantity: safeNumber(currentItem.quantity, 0),
            unitRate: origValues?.unitRate ?? getLaborUnitRate(currentItem),
            laborHours: safeNumber(currentItem.laborHours, 0),
          }),
        }));
      }
    }
  }, [currentIndex, currentItem?._id]);

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleVarianceChange = (field, value) => {
    setVarianceData((prev) => ({
      ...prev,
      [field]:
        field === "requiredQty" || field === "quantity" ? Number(value) : value,
    }));
  };

  const toggleLaborItem = (laborId) => {
    setSelectedLaborItems((prev) => ({
      ...prev,
      [laborId]: !prev[laborId],
    }));
  };

  // Updated saveCurrentItemChanges function
  const saveCurrentItemChanges = () => {
    if (!currentItem) {
      console.warn("saveCurrentItemChanges: currentItem is null/undefined");
      return null;
    }

    const effectiveItemId =
      currentItem._id ||
      currentItem.key ||
      currentItem.originalLineItemId ||
      currentItem.lineItemId;

    if (!effectiveItemId || !currentItem.assemblyId) {
      console.error("Cannot save change - missing critical IDs", {
        effectiveItemId,
        assemblyId: currentItem.assemblyId,
        currentIndex,
      });
      return null;
    }

    const changes = calculateChanges(currentItem, varianceData);
    const isMaterial = currentItem.category === "M";
    const origValues = getOriginalValues(currentItem);

    const laborVariances = linkedLaborItems
      .filter((labor) => selectedLaborItems[labor._id])
      .map((labor) => ({
        assemblyId: String(labor.assemblyId || currentItem.assemblyId),
        lineItemId: String(labor._id),
        laborId: labor.laborId,
        coverageDelta: changes.coverageDelta,
        unitRate: labor.unitRate || labor.price || 0,
        description: labor.costCodeDescription || labor.itemDescription,
        bidArea: labor.bidArea,
      }));

    return {
      assemblyId: String(currentItem.assemblyId),
      lineItemId: String(effectiveItemId),
      category: currentItem.category || "M",
      deltaQty: changes.sizeChanged
        ? (varianceData.requiredQty ?? currentItem.requiredQty ?? 0)
        : changes.deltaQty,
      newSpec: changes.hasSpecChange ? changes.newSpec : {},
      savedVarianceData: { ...varianceData },
      savedSelectedLaborItems: { ...selectedLaborItems },
      editedValues: changes.editedValues,
      sizeChanged: changes.sizeChanged,
      priceChanged: changes.priceChanged,
      vendorChanged: changes.vendorChanged,
      coverageDelta: changes.coverageDelta,
      originalCoverage: changes.originalCoverage,
      newCoverage: changes.newCoverage,
      laborVariances: laborVariances.length > 0 ? laborVariances : undefined,
      costDiff: changes.costDiff,
      netRemaining: changes.netRemaining,
      closeoutAmount: changes.closeoutAmount,
      newSpecAmount: changes.newSpecAmount,
      originalPrice: origValues?.cost ?? origValues?.unitRate ?? 0,
      newPrice: isMaterial
        ? safeNumber(varianceData.cost, origValues?.cost)
        : safeNumber(varianceData.unitRate, origValues?.unitRate),
      originalVendorId: changes.originalVendorId,
      // Include reduction info for backend validation
      isReduction: changes.isReduction,
      minAllowedQty: changes.minAllowedQty,
      originalQty: changes.originalQty,
    };
  };

  const updateAllChanges = (changeEntry) => {
    if (!changeEntry) return changeEntry;

    const prev = allChangesRef.current;
    const existingIndex = prev.findIndex(
      (c) => c.lineItemId === changeEntry.lineItemId,
    );

    let updated;
    if (existingIndex >= 0) {
      updated = [...prev];
      updated[existingIndex] = changeEntry;
    } else {
      updated = [...prev, changeEntry];
    }

    allChangesRef.current = updated;
    setAllChanges(updated);

    return changeEntry;
  };

  // Calculate labor variance impact based on coverage delta
  const calculateLaborVarianceImpact = (coverageDelta, laborItem) => {
    if (!laborItem || Math.abs(coverageDelta) < 0.001) return 0;
    const unitRate = laborItem.unitRate || laborItem.price || 0;
    return Number((coverageDelta * unitRate).toFixed(2));
  };

  // Calculate preview of changes for current item
  const currentChanges = currentItem
    ? calculateChanges(currentItem, varianceData)
    : {
        deltaQty: 0,
        costDiff: 0,
        coverageDelta: 0,
        newSpec: {},
        hasSpecChange: false,
        sizeChanged: false,
        priceChanged: false,
        vendorChanged: false,
        netRemaining: 0,
        hasAnyChange: false,
        minAllowedQty: 0,
        isReduction: false,
        isBelowMinimum: false,
      };

  // Debug: log when current item changes to verify net data lookup
  useEffect(() => {
    if (currentItem) {
      const netData = getNetItemData(currentItem);
      console.log(`[VarianceModal] Current item:`, {
        _id: currentItem._id,
        originalLineItemId: currentItem.originalLineItemId,
        key: currentItem.key,
        description: (
          currentItem.itemDescription ||
          currentItem.costCodeDescription ||
          ""
        ).substring(0, 50),
        // Enrichment status
        _hasNetData: currentItem._hasNetData,
        isEnriched: currentItem._hasNetData === true,
        // Net data lookup result
        netDataFound: !!netData,
        netData: netData || "NOT FOUND",
        // Item's own quantity data (should be populated if enriched)
        itemQtyOrdered: currentItem.qtyOrdered,
        itemQtyPending: currentItem.qtyPending,
        itemNetRemaining: currentItem.netRemaining,
        // Calculated values
        currentChangesQtyOrdered: currentChanges.qtyOrdered,
        currentChangesQtyPending: currentChanges.qtyPending,
        currentChangesMinAllowedQty: currentChanges.minAllowedQty,
      });
    }
  }, [currentItem?._id, projectNetQuantities, currentChanges]);

  // Calculate total labor variance impact
  const totalLaborVarianceImpact = useMemo(() => {
    if (!currentChanges.coverageDelta || linkedLaborItems.length === 0)
      return 0;

    return linkedLaborItems.reduce((sum, labor) => {
      if (!selectedLaborItems[labor._id]) return sum;
      return (
        sum + calculateLaborVarianceImpact(currentChanges.coverageDelta, labor)
      );
    }, 0);
  }, [currentChanges.coverageDelta, linkedLaborItems, selectedLaborItems]);

  // Updated handleNext with reduction validation
  const handleNext = () => {
    if (!varianceData.varianceTitle?.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Variance Title is required.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (!varianceData.varianceDescription?.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Variance Reason is required.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (!currentItem) {
      console.error("[handleNext] currentItem is null/undefined", {
        currentIndex,
      });
      setDialog({
        open: true,
        type: "error",
        title: "Data Error",
        message: "Current item is missing. Please refresh the page.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    const effectiveItemId =
      currentItem._id ||
      currentItem.key ||
      currentItem.originalLineItemId ||
      currentItem.lineItemId;

    if (!effectiveItemId || !currentItem.assemblyId) {
      console.error("[handleNext] Missing critical identifiers", {
        currentIndex,
        effectiveItemId,
        hasAssemblyId: !!currentItem.assemblyId,
      });

      setDialog({
        open: true,
        type: "warning",
        title: "Skipping Item",
        message:
          "This item is missing required identifiers. Skipping to next item if available.",
        confirmText: "Continue",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          if (!isLastItem) {
            setCurrentIndex((prev) => prev + 1);
          } else {
            if (allChangesRef.current.length > 0) {
              handleCreateVariance([...allChangesRef.current]);
            } else {
              setDialog({
                open: true,
                type: "info",
                title: "Nothing to Submit",
                message: "No valid changes were saved.",
                onConfirm: () =>
                  setDialog((prev) => ({ ...prev, open: false })),
              });
            }
          }
        },
      });
      return;
    }

    const changes = calculateChanges(currentItem, varianceData);
    const isMaterial = currentItem.category === "M";

    // Validate reduction doesn't go below minimum
    if (changes.isReduction && changes.isBelowMinimum) {
      const qtyField = isMaterial ? "requiredQty" : "quantity";
      setDialog({
        open: true,
        type: "error",
        title: "Invalid Quantity Reduction",
        message: `Cannot reduce quantity below ${changes.minAllowedQty} (already ordered: ${changes.qtyOrdered}, pending: ${changes.qtyPending}). The minimum allowed quantity is what has already been committed to orders.`,
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          // Auto-correct to minimum allowed
          handleVarianceChange(qtyField, changes.minAllowedQty);
        },
      });
      return;
    }

    let hasChanges;
    if (isMaterial) {
      hasChanges =
        changes.sizeChanged ||
        (changes.priceChanged && changes.netRemaining > 0) ||
        Math.abs(changes.deltaQty) > 0.001 ||
        changes.hasSpecChange ||
        changes.vendorChanged;
    } else {
      hasChanges =
        Math.abs(changes.costDiff) > 0.01 ||
        Math.abs(changes.deltaQty) > 0.001 ||
        changes.hasSpecChange;
    }

    if (!hasChanges) {
      setDialog({
        open: true,
        type: "warning",
        title: "No Changes",
        message:
          "No modifications detected for this item. Please make changes or skip.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    const changeEntry = saveCurrentItemChanges();

    if (!changeEntry) {
      console.warn("[handleNext] Failed to create change entry", {
        currentIndex,
        effectiveItemId,
      });
      setDialog({
        open: true,
        type: "warning",
        title: "Save Skipped",
        message: "Could not save changes for this item. Continuing anyway...",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } else {
      console.log("[handleNext] Saved change entry:", {
        assemblyId: changeEntry.assemblyId,
        lineItemId: changeEntry.lineItemId,
        currentIndex,
        hasLabor: !!changeEntry.laborVariances?.length,
        vendorChanged: changeEntry.vendorChanged,
        isReduction: changeEntry.isReduction,
        deltaQty: changeEntry.deltaQty,
      });
      updateAllChanges(changeEntry);
    }

    const proceed = () => {
      if (!isLastItem) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        console.log(
          "[handleNext] Final changes before submission:",
          allChangesRef.current.map((c) => ({
            assemblyId: c.assemblyId,
            lineItemId: c.lineItemId,
            vendorChanged: c.vendorChanged,
            isReduction: c.isReduction,
            deltaQty: c.deltaQty,
          })),
        );
        handleCreateVariance([...allChangesRef.current]);
      }
    };

    // Size change + labor warning
    if (changes.sizeChanged && linkedLaborItems.length > 0) {
      const selectedCount =
        Object.values(selectedLaborItems).filter(Boolean).length;
      if (selectedCount === 0) {
        setDialog({
          open: true,
          type: "warning",
          title: "Labor Not Included",
          message:
            "Size change detected but no labor items selected. Labor costs won't adjust. Continue without labor?",
          confirmText: "Continue Without Labor",
          cancelText: "Go Back",
          onConfirm: () => {
            setDialog((prev) => ({ ...prev, open: false }));
            proceed();
          },
          onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
    }

    proceed();
  };

  // Updated handleBack
  const handleBack = () => {
    if (currentIndex <= 0) return;

    if (!currentItem) {
      console.warn("[handleBack] currentItem is null/undefined", {
        currentIndex,
      });
      setCurrentIndex((prev) => prev - 1);
      return;
    }

    const effectiveItemId =
      currentItem._id ||
      currentItem.key ||
      currentItem.originalLineItemId ||
      currentItem.lineItemId;

    if (!effectiveItemId || !currentItem.assemblyId) {
      console.warn("[handleBack] Skipping save - missing identifiers", {
        currentIndex,
        effectiveItemId,
        hasAssemblyId: !!currentItem.assemblyId,
      });
      setCurrentIndex((prev) => prev - 1);
      return;
    }

    const changeEntry = saveCurrentItemChanges();

    if (changeEntry) {
      console.log("[handleBack] Saved change entry:", {
        assemblyId: changeEntry.assemblyId,
        lineItemId: changeEntry.lineItemId,
        currentIndex,
      });
      updateAllChanges(changeEntry);
    }

    setCurrentIndex((prev) => prev - 1);
  };

  // Updated handleCreateVariance
  const handleCreateVariance = async (changesToSubmit) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found.");
      }

      const validChanges = changesToSubmit.filter((c) => {
        const originalItem = currentItems.find(
          (item) =>
            item._id === c.lineItemId ||
            item.key === c.lineItemId ||
            item.originalLineItemId === c.lineItemId,
        );
        const isMaterial = originalItem?.category === "M" || c.category === "M";

        if (isMaterial) {
          return (
            c.sizeChanged ||
            (c.priceChanged && c.netRemaining > 0) ||
            Math.abs(c.deltaQty) > 0.001 ||
            Object.keys(c.newSpec || {}).length > 0 ||
            c.vendorChanged ||
            (c.laborVariances && c.laborVariances.length > 0)
          );
        } else {
          return (
            Math.abs(c.costDiff) > 0.01 ||
            Math.abs(c.deltaQty) > 0.001 ||
            Object.keys(c.newSpec || {}).length > 0
          );
        }
      });

      if (validChanges.length === 0) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: "No valid changes to submit.",
          onConfirm: () => {
            setDialog((prev) => ({ ...prev, open: false }));
            setIsSubmitting(false);
          },
        });
        return;
      }

      // Build payload matching backend /variance endpoint expectations
      const payload = {
        varianceReason: varianceData.varianceDescription.trim(),
        items: validChanges.map((change) => ({
          assemblyId: change.assemblyId,
          lineItemId: change.lineItemId,

          // Explicit semantic fields
          sizeChanged: !!change.sizeChanged,
          priceChanged: !!change.priceChanged,
          vendorChanged: !!change.vendorChanged,
          isReduction: !!change.isReduction,

          // Quantity
          deltaQty: change.deltaQty,
          minAllowedQty: change.minAllowedQty,
          originalQty: change.originalQty,

          // Coverage info
          originalCoverage: change.originalCoverage,
          newCoverage: change.newCoverage,
          netRemaining: change.netRemaining,

          // Price change support
          originalPrice: change.originalPrice,
          newPrice: change.newPrice,

          // Vendor (always include if present in newSpec)
          ...(change.newSpec?.vendor !== undefined && {
            vendor: change.newSpec.vendor,
          }),

          // Other spec changes
          ...(change.newSpec?.size && { size: change.newSpec.size }),
          ...(change.newSpec?.price !== undefined && {
            price: change.newSpec.price,
          }),
          ...(change.newSpec?.cost !== undefined && {
            cost: change.newSpec.cost,
          }),
          ...(change.newSpec?.coveragePerUnit && {
            coveragePerUnit: change.newSpec.coveragePerUnit,
          }),
          ...(change.newSpec?.unitRate !== undefined && {
            unitRate: change.newSpec.unitRate,
          }),
        })),

        // Labor coverage adjustments
        laborVariances: validChanges
          .flatMap((c) => c.laborVariances || [])
          .filter((lv) => Math.abs(lv.coverageDelta) > 0.001),
      };

      console.log(
        "Submitting variance payload:",
        JSON.stringify(payload, null, 2),
      );

      await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/budget2/${projectId}/variance`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 20000,
        },
      );

      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setProject(updatedProjectResponse.data);

      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Variance created successfully!",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          onVarianceCreated?.(updatedProjectResponse.data);
          onClose();
        },
      });
    } catch (err) {
      console.error("Variance creation error:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Unknown error creating variance";

      setDialog({
        open: true,
        type: "error",
        title: "Failed to Create Variance",
        message,
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          setIsSubmitting(false);
        },
      });
    }
  };

  // Get original values for display
  const origValues = getOriginalValues(currentItem);

  // Get current vendor ID for display
  const currentVendorId = getVendorId(currentItem?.vendor);

  // If all items were closed out, show message
  if (currentItems.length === 0) {
    return (
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-[15000]">
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-md border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-4">
            Cannot Create Variance
          </h3>
          <p className="text-gray-300 mb-4">
            {closedOutCount === 1
              ? "The selected item has been closed out due to a size change and cannot have variances created."
              : `All ${closedOutCount} selected items have been closed out due to size changes and cannot have variances created.`}
          </p>
          <p className="text-sm text-gray-400 mb-6">
            Closed-out items have been replaced by new items with different
            sizes. To make changes, please select the replacement items instead.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Loading state while fetching net quantities
  if (isLoadingNetQty) {
    return (
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-[15000]">
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-md border border-gray-600">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-400"></div>
            <span className="ml-3 text-gray-200">Loading budget data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-[15000]">
      <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-5xl max-h-[95vh] overflow-y-auto border border-gray-600">
        <h3 className="text-lg font-semibold text-white mb-4">
          Create Variance{" "}
          {currentItems.length > 1
            ? `(${currentIndex + 1} of ${currentItems.length})`
            : ""}
        </h3>

        {/* Warning if some items were filtered out */}
        {closedOutCount > 0 && (
          <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-500/50 rounded-lg">
            <p className="text-sm text-yellow-400">
              ⚠ {closedOutCount} item{closedOutCount !== 1 ? "s were" : " was"}{" "}
              skipped because {closedOutCount !== 1 ? "they have" : "it has"}{" "}
              been closed out due to size changes.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {/* Variance Title */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Variance Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={varianceData.varianceTitle || ""}
              onChange={(e) =>
                handleVarianceChange("varianceTitle", e.target.value)
              }
              placeholder="Enter a title for this variance"
              className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              required
            />
          </div>

          {/* Variance Reason */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Variance Reason <span className="text-red-400">*</span>
            </label>
            <textarea
              value={varianceData.varianceDescription || ""}
              onChange={(e) =>
                handleVarianceChange("varianceDescription", e.target.value)
              }
              placeholder="Explain why this variance is needed"
              className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              rows="2"
              required
            />
          </div>

          {/* Current Item Info Banner */}
          {currentItem && (
            <div className="col-span-2 bg-gray-800 rounded-lg p-3 mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <span
                    className={`inline-block px-2 py-0.5 text-xs font-semibold rounded mr-2 ${
                      currentItem.category === "M"
                        ? "bg-blue-900/50 text-blue-300"
                        : "bg-orange-900/50 text-orange-300"
                    }`}
                  >
                    {currentItem.category === "M" ? "Material" : "Labor"}
                  </span>
                  <span className="text-sm text-gray-300">
                    {currentItem.conditionName && (
                      <span className="text-gray-500">
                        {currentItem.conditionName} /{" "}
                      </span>
                    )}
                    {currentItem.sectionNumber}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  Item #{currentItem.itemNumber || "N/A"}
                </span>
              </div>
              <p className="text-sm text-gray-200 mt-1">
                {currentItem.itemDescription ||
                  currentItem.costCodeDescription ||
                  "No description"}
              </p>
              <div className="flex items-center gap-4 mt-1">
                {currentItem.category === "M" &&
                  currentItem.coveragePerUnit && (
                    <p className="text-xs text-teal-400">
                      Coverage: {Number(currentItem.coveragePerUnit).toFixed(2)}{" "}
                      {currentItem.uom}
                    </p>
                  )}

                {currentItem.laborId !== "-" && currentItem.laborId && (
                  <p className="text-xs text-purple-400">
                    Labor ID: {currentItem.laborId}
                  </p>
                )}
              </div>
              {/* Net Quantity Info */}
              {currentItem.category === "M" && (
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-gray-500">
                      Budget:{" "}
                      <span className="text-blue-400">
                        {currentChanges.netRequiredQty}
                      </span>
                    </span>
                    <span className="text-gray-500">
                      Ordered:{" "}
                      <span
                        className={`${currentChanges.qtyOrdered > 0 ? "text-green-400 font-medium" : "text-green-400"}`}
                      >
                        {currentChanges.qtyOrdered}
                      </span>
                    </span>
                    <span className="text-gray-500">
                      Pending:{" "}
                      <span
                        className={`${currentChanges.qtyPending > 0 ? "text-yellow-400 font-medium" : "text-yellow-400"}`}
                      >
                        {currentChanges.qtyPending}
                      </span>
                    </span>
                    <span className="text-gray-500">
                      Remaining:{" "}
                      <span className="text-orange-400">
                        {currentChanges.netRemaining}
                      </span>
                    </span>
                    {/* Show minimum allowed for reductions */}
                    {currentChanges.minAllowedQty > 0 && (
                      <span className="text-gray-500">
                        Min Qty:{" "}
                        <span className="text-red-400 font-medium">
                          {currentChanges.minAllowedQty}
                        </span>
                      </span>
                    )}
                  </div>
                  {/* Show warning if using fallback data */}
                  {!getNetItemData(currentItem) &&
                    (currentItem.qtyOrdered > 0 ||
                      currentItem.qtyPending > 0) && (
                      <p className="text-[10px] text-amber-500 mt-1">
                        ⚠ Using item data (net lookup not found)
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Material Item Fields */}
          {currentItem?.category === "M" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Size
                  {usesCoverage(currentItem.container || currentItem.uom) && (
                    <span className="ml-1 text-xs text-teal-400">
                      (affects coverage)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={varianceData.size ?? currentItem.size ?? ""}
                  onChange={(e) => handleVarianceChange("size", e.target.value)}
                  placeholder="Size (e.g., 4'x8')"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: {origValues?.size || currentItem.size || "N/A"}
                  {currentChanges.sizeChanged && (
                    <span className="ml-2 text-yellow-400">
                      Coverage: {currentChanges.originalCoverage?.toFixed(2)} →{" "}
                      {currentChanges.newCoverage?.toFixed(2)}
                    </span>
                  )}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Bid Area
                </label>
                <Select
                  options={bidAreaOptions}
                  value={
                    bidAreaOptions.find(
                      (option) =>
                        option.value ===
                        (varianceData.bidArea ?? currentItem.bidArea),
                    ) || null
                  }
                  onChange={(selected) =>
                    handleVarianceChange(
                      "bidArea",
                      selected ? selected.value : "",
                    )
                  }
                  placeholder="Select Bid Area"
                  className="w-full"
                  styles={customStyles}
                  isClearable
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original:{" "}
                  {origValues?.bidArea || currentItem.bidArea || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Vendor
                  {currentChanges.vendorChanged && (
                    <span className="ml-1 text-xs text-cyan-400">
                      (changed)
                    </span>
                  )}
                </label>
                <Select
                  options={vendorOptions}
                  value={
                    vendorOptions.find(
                      (option) =>
                        option.value ===
                        (varianceData.vendor ?? currentVendorId),
                    ) || null
                  }
                  onChange={(selected) =>
                    handleVarianceChange(
                      "vendor",
                      selected ? selected.value : null,
                    )
                  }
                  placeholder="Select Vendor"
                  className="w-full"
                  styles={customStyles}
                  isClearable
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original:{" "}
                  {vendorOptions.find(
                    (option) =>
                      option.value === (origValues?.vendor || currentVendorId),
                  )?.label || "Unassigned"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Required Quantity
                  {currentChanges.sizeChanged && (
                    <span className="ml-1 text-xs text-yellow-400">
                      (new spec qty)
                    </span>
                  )}
                  {currentChanges.isReduction &&
                    !currentChanges.sizeChanged && (
                      <span className="ml-1 text-xs text-red-400">
                        (reduction)
                      </span>
                    )}
                </label>
                <input
                  type="number"
                  value={
                    varianceData.requiredQty ?? currentItem.requiredQty ?? 0
                  }
                  onChange={(e) =>
                    handleVarianceChange("requiredQty", Number(e.target.value))
                  }
                  min={currentChanges.minAllowedQty}
                  placeholder="Required Quantity"
                  className={`w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm ${
                    currentChanges.isBelowMinimum ? "border-red-500" : ""
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original:{" "}
                  {origValues?.requiredQty ?? currentItem.requiredQty ?? 0}
                  {currentChanges.minAllowedQty > 0 && (
                    <span className="ml-2 text-red-400">
                      Min: {currentChanges.minAllowedQty} (ordered + pending)
                    </span>
                  )}
                  {currentChanges.sizeChanged &&
                    currentChanges.netRemaining > 0 && (
                      <span className="ml-2 text-teal-400">
                        (Auto-suggest:{" "}
                        {Math.ceil(
                          (currentChanges.netRemaining *
                            currentChanges.originalCoverage) /
                            currentChanges.newCoverage,
                        )}{" "}
                        for equiv. coverage)
                      </span>
                    )}
                </p>
                {/* Warning for below minimum */}
                {currentChanges.isBelowMinimum && (
                  <p className="text-xs text-red-400 mt-1">
                    ⚠ Quantity cannot go below {currentChanges.minAllowedQty}{" "}
                    (already committed to orders)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Cost (per unit)
                  {currentChanges.priceChanged &&
                    !currentChanges.sizeChanged && (
                      <span className="ml-1 text-xs text-cyan-400">
                        (price change)
                      </span>
                    )}
                </label>
                <NumericFormat
                  value={varianceData.cost ?? origValues?.cost ?? 0}
                  onValueChange={(values) =>
                    handleVarianceChange("cost", values.floatValue)
                  }
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  thousandSeparator={false}
                  placeholder="0.00"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: ${formatNumber(origValues?.cost ?? 0)}
                  {currentChanges.priceChanged &&
                    currentChanges.qtyOrdered > 0 && (
                      <span className="ml-2 text-cyan-400">
                        ({currentChanges.qtyOrdered} already ordered at original
                        price)
                      </span>
                    )}
                </p>
              </div>
            </>
          )}

          {/* Labor Item Fields */}
          {currentItem?.category === "L" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Cost Code Description
                </label>
                <input
                  type="text"
                  value={
                    varianceData.costCodeDescription ??
                    currentItem.costCodeDescription ??
                    ""
                  }
                  onChange={(e) =>
                    handleVarianceChange("costCodeDescription", e.target.value)
                  }
                  placeholder="Cost Code Description"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: {currentItem.costCodeDescription || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit of Measure
                </label>
                <input
                  type="text"
                  value={varianceData.uom ?? currentItem.uom ?? ""}
                  onChange={(e) => handleVarianceChange("uom", e.target.value)}
                  placeholder="Unit of Measure"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: {currentItem.uom || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Quantity
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={varianceData.quantity ?? origValues?.quantity ?? 0}
                  onChange={(e) =>
                    handleVarianceChange("quantity", Number(e.target.value))
                  }
                  placeholder="Quantity"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: {origValues?.quantity ?? currentItem.quantity ?? 0}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit Rate
                </label>
                <NumericFormat
                  value={varianceData.unitRate ?? origValues?.unitRate ?? 0}
                  onValueChange={(values) =>
                    handleVarianceChange("unitRate", values.floatValue)
                  }
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  thousandSeparator={false}
                  placeholder="0.00"
                  className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Original: ${formatNumber(origValues?.unitRate ?? 0)}
                </p>
              </div>
            </>
          )}

          {/* Linked Labor Items Section (for Materials) */}
          {currentItem?.category === "M" &&
            currentItem.laborId &&
            (isLoadingLabor || linkedLaborItems.length > 0) && (
              <div className="col-span-2 border-t border-gray-600 pt-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    Linked Labor Items (Labor ID: {currentItem.laborId})
                  </h4>
                  {linkedLaborItems.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {Object.values(selectedLaborItems).filter(Boolean).length}{" "}
                      of {linkedLaborItems.length} selected
                    </span>
                  )}
                </div>

                {isLoadingLabor ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-400"></div>
                    <span className="ml-2 text-sm text-gray-400">
                      Loading linked labor...
                    </span>
                  </div>
                ) : linkedLaborItems.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    No linked labor items found in this assembly.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 mb-2">
                      Select labor items to include in the variance. Labor costs
                      will be adjusted based on coverage change.
                    </p>
                    {linkedLaborItems.map((labor) => {
                      const laborImpact = calculateLaborVarianceImpact(
                        currentChanges.coverageDelta,
                        labor,
                      );
                      const isSelected = selectedLaborItems[labor._id];

                      return (
                        <div
                          key={labor._id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? "bg-purple-900/30 border-purple-500/50"
                              : "bg-gray-800/50 border-gray-600 hover:border-gray-500"
                          }`}
                          onClick={() => toggleLaborItem(labor._id)}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleLaborItem(labor._id)}
                              className="mt-1 h-4 w-4 text-purple-500 focus:ring-purple-500 border-gray-500 rounded"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-200">
                                  {labor.costCodeDescription ||
                                    labor.itemDescription ||
                                    "Labor Item"}
                                </p>
                                <span className="text-xs text-gray-500">
                                  #{labor.itemNumber || "N/A"}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                                <span>
                                  Rate: ${formatNumber(labor.unitRate || 0)}
                                </span>
                                <span>Current Qty: {labor.quantity || 0}</span>
                                {labor.bidArea && (
                                  <span>Area: {labor.bidArea}</span>
                                )}
                              </div>
                              {currentChanges.coverageDelta !== 0 && (
                                <div className="mt-2 text-xs">
                                  <span className="text-gray-500">
                                    Coverage Δ:{" "}
                                  </span>
                                  <span
                                    className={
                                      currentChanges.coverageDelta > 0
                                        ? "text-green-400"
                                        : "text-red-400"
                                    }
                                  >
                                    {currentChanges.coverageDelta > 0
                                      ? "+"
                                      : ""}
                                    {currentChanges.coverageDelta.toFixed(2)}
                                  </span>
                                  <span className="text-gray-500 mx-2">→</span>
                                  <span className="text-gray-500">
                                    Labor Impact:{" "}
                                  </span>
                                  <span
                                    className={
                                      laborImpact > 0
                                        ? "text-red-400"
                                        : laborImpact < 0
                                          ? "text-green-400"
                                          : "text-gray-400"
                                    }
                                  >
                                    {laborImpact > 0 ? "+" : ""}$
                                    {formatNumber(laborImpact)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          {/* Change Summary */}
          <div
            className={`col-span-2 grid gap-2 text-sm ${
              currentItem?.category === "M"
                ? usesCoverage(currentItem?.container || currentItem?.uom)
                  ? "grid-cols-4"
                  : "grid-cols-3"
                : "grid-cols-2"
            }`}
          >
            {/* Qty Change */}
            <div
              className={`p-2 rounded ${
                currentChanges.sizeChanged || currentChanges.deltaQty !== 0
                  ? currentChanges.isReduction && !currentChanges.sizeChanged
                    ? "bg-red-900/30 border border-red-500/50"
                    : "bg-blue-900/30 border border-blue-500/50"
                  : "bg-gray-800"
              }`}
            >
              <span className="text-gray-400">
                {currentChanges.sizeChanged
                  ? "New Qty:"
                  : currentChanges.isReduction
                    ? "Qty Reduction:"
                    : "Qty Δ:"}
              </span>
              <span
                className={`ml-2 font-medium ${
                  currentChanges.sizeChanged
                    ? "text-blue-400"
                    : currentChanges.deltaQty > 0
                      ? "text-green-400"
                      : currentChanges.deltaQty < 0
                        ? "text-red-400"
                        : "text-gray-400"
                }`}
              >
                {currentChanges.sizeChanged
                  ? (varianceData.requiredQty ?? currentItem?.requiredQty ?? 0)
                  : (currentChanges.deltaQty > 0 ? "+" : "") +
                    currentChanges.deltaQty}
              </span>
            </div>

            {/* Coverage Δ - only for materials with coverage UoM */}
            {currentItem?.category === "M" &&
              usesCoverage(currentItem?.container || currentItem?.uom) && (
                <div
                  className={`p-2 rounded ${
                    currentChanges.coverageDelta !== 0
                      ? "bg-teal-900/30 border border-teal-500/50"
                      : "bg-gray-800"
                  }`}
                >
                  <span className="text-gray-400">Coverage Δ:</span>
                  <span
                    className={`ml-2 font-medium ${
                      currentChanges.coverageDelta > 0
                        ? "text-green-400"
                        : currentChanges.coverageDelta < 0
                          ? "text-red-400"
                          : "text-gray-400"
                    }`}
                  >
                    {currentChanges.coverageDelta > 0 ? "+" : ""}
                    {currentChanges.coverageDelta.toFixed(2)}
                  </span>
                </div>
              )}

            {/* Main Cost Impact (Material or Labor) */}
            <div
              className={`p-2 rounded ${
                currentChanges.costDiff !== 0
                  ? currentChanges.costDiff < 0
                    ? "bg-green-900/30 border border-green-500/50"
                    : "bg-orange-900/30 border border-orange-500/50"
                  : "bg-gray-800"
              }`}
            >
              <span className="text-gray-400">
                {currentItem?.category === "M" ? "Material" : "Labor"} Cost
                Impact:
              </span>
              <span
                className={`ml-2 font-medium ${
                  currentChanges.costDiff > 0
                    ? "text-red-400"
                    : currentChanges.costDiff < 0
                      ? "text-green-400"
                      : "text-gray-400"
                }`}
              >
                {currentChanges.costDiff > 0 ? "+" : ""}$
                {formatNumber(currentChanges.costDiff)}
                {currentChanges.costDiff < 0 && (
                  <span className="ml-1 text-xs">(savings)</span>
                )}
              </span>
            </div>

            {/* Linked Labor Cost - only for materials */}
            {currentItem?.category === "M" && (
              <div
                className={`p-2 rounded ${
                  totalLaborVarianceImpact !== 0
                    ? "bg-purple-900/30 border border-purple-500/50"
                    : "bg-gray-800"
                }`}
              >
                <span className="text-gray-400">Labor Cost:</span>
                <span
                  className={`ml-2 font-medium ${
                    totalLaborVarianceImpact > 0
                      ? "text-red-400"
                      : totalLaborVarianceImpact < 0
                        ? "text-green-400"
                        : "text-gray-400"
                  }`}
                >
                  {totalLaborVarianceImpact > 0 ? "+" : ""}$
                  {formatNumber(totalLaborVarianceImpact)}
                </span>
                {linkedLaborItems.length === 0 && (
                  <span className="block text-[10px] text-gray-500 mt-1">
                    No linked labor
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="col-span-2 flex justify-between space-x-2 mt-4">
            <div className="text-xs text-gray-500">
              {allChanges.length > 0 && (
                <span>
                  {allChanges.length} item{allChanges.length !== 1 ? "s" : ""}{" "}
                  with changes saved
                </span>
              )}
            </div>
            <div className="flex space-x-2">
              {currentIndex > 0 && (
                <button
                  onClick={handleBack}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Back
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                disabled={isSubmitting || currentChanges.isBelowMinimum}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                  isSubmitting || currentChanges.isBelowMinimum
                    ? "bg-orange-300 cursor-not-allowed"
                    : "bg-orange-400 hover:bg-orange-500"
                }`}
              >
                {isSubmitting
                  ? "Creating..."
                  : isLastItem
                    ? "Create Variance"
                    : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VarianceModal;
